/**
 * Aggregate diagnostics, on by default and switchable in Settings.
 *
 * The bar this has to clear: nothing sent may identify a person or reveal what they
 * chose to listen to. What is left is genuinely useful — whether assembly stalled, which
 * container the device could decode, how far into sits people get — and is enough to
 * tell whether the audio engine works on hardware we do not own.
 *
 * Sends are best-effort and never block anything. If the user turns them off, this
 * module does nothing at all. Full session traces deliberately use `diagnostics.ts`
 * because they do not meet the privacy bar above.
 */

import { settings } from './state/settings';
import { apiOrigin } from './config/origin';

export interface Beacon {
  event: 'session_start' | 'session_end' | 'session_incomplete'
    | 'stall' | 'qa_fail' | 'capability';
  targetBucketMin?: number;
  cadence?: 'opening' | 'occasional' | 'frequent';
  completion?: number;
  container?: string;
  engine?: string;
  amRatio?: number;
  reason?: string;
  outcome?: 'completed' | 'ended-early' | 'interrupted' | 'abandoned';
}

// Resolved per call rather than at module load: the native shell serves this page from
// https://localhost, where a relative path reaches the APK's own asset bundle and 404s.
// Nothing then reports — no telemetry, no trace, no way to tell a silent app from an
// unused one. `apiOrigin()` is the empty string on the web, so the URL is unchanged there.
const endpoint = (): string => `${apiOrigin()}/api/telemetry`;

/**
 * Coarse platform bucket. Not a fingerprint — six possible values.
 *
 * Windows and Linux were added after the first real traffic arrived and 8 of 44 sits
 * reported `other`: the classifier had no Windows bucket at all, so the largest desktop
 * platform was invisible inside a catch-all. `other` should mean "we do not know", not
 * "we did not ask".
 */
function platform(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Macintosh/.test(ua)) return 'macos';
  if (/Windows/.test(ua)) return 'windows';
  if (/Linux|X11|CrOS/.test(ua)) return 'linux';
  return 'other';
}

export function report(b: Beacon): void {
  if (!settings.value.telemetryEnabled) return;

  const body = JSON.stringify({ ...b, platform: platform() });
  try {
    // sendBeacon survives the page being backgrounded mid-sit, which is exactly when
    // the interesting events happen.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(endpoint(), new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch(endpoint(), { method: 'POST', body, keepalive: true,
      headers: { 'content-type': 'application/json' } });
  } catch {
    /* diagnostics must never break a sit */
  }
}
